import type { CampaignType } from '../types';

export const CAMPAIGN_CONCURRENCY_OVERCOMMIT_ERROR_CODE = 'campaign_concurrency_overcommit';

export interface CampaignConcurrencyConflictRow {
  id: string;
  name: string;
  campaignType?: CampaignType | string | null;
  status?: string | null;
  maxConcurrentCalls: number;
}

export interface CampaignConcurrencyConflictPayload {
  errorCode: string;
  error?: string;
  campaignType?: CampaignType | string | null;
  limit: number;
  activeConcurrency: number;
  requestedConcurrency: number;
  projectedConcurrency: number;
  excessConcurrency: number;
  campaign?: CampaignConcurrencyConflictRow | null;
  activeCampaigns: CampaignConcurrencyConflictRow[];
}

type RawCampaignConcurrencyConflictRow = {
  id?: unknown;
  name?: unknown;
  campaignType?: unknown;
  campaign_type?: unknown;
  status?: unknown;
  maxConcurrentCalls?: unknown;
  max_concurrent_calls?: unknown;
};

type RawCampaignConcurrencyConflictPayload = {
  errorCode?: unknown;
  error_code?: unknown;
  error?: unknown;
  campaignType?: unknown;
  campaign_type?: unknown;
  limit?: unknown;
  activeConcurrency?: unknown;
  active_concurrency?: unknown;
  requestedConcurrency?: unknown;
  requested_concurrency?: unknown;
  projectedConcurrency?: unknown;
  projected_concurrency?: unknown;
  excessConcurrency?: unknown;
  excess_concurrency?: unknown;
  campaign?: RawCampaignConcurrencyConflictRow | null;
  activeCampaigns?: RawCampaignConcurrencyConflictRow[];
  active_campaigns?: RawCampaignConcurrencyConflictRow[];
};

function toNumber(value: unknown): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeCampaignRow(
  row: RawCampaignConcurrencyConflictRow | null | undefined,
): CampaignConcurrencyConflictRow | null {
  if (!row) {
    return null;
  }
  return {
    id: String(row.id ?? ''),
    name: String(row.name ?? ''),
    campaignType: (row.campaignType ?? row.campaign_type ?? null) as CampaignType | string | null,
    status: row.status == null ? null : String(row.status),
    maxConcurrentCalls: toNumber(row.maxConcurrentCalls ?? row.max_concurrent_calls),
  };
}

/**
 * Extract the structured campaign-concurrency conflict from an Axios error.
 *
 * Error responses are not always transformed by the shared interceptor, so this
 * parser accepts both snake_case and camelCase payloads before normalizing the
 * shape consumed by the dialog.
 */
export function getCampaignConcurrencyConflict(
  error: unknown,
): CampaignConcurrencyConflictPayload | null {
  const data = (error as { response?: { data?: RawCampaignConcurrencyConflictPayload } })?.response?.data;
  const errorCode = String(data?.errorCode ?? data?.error_code ?? '');
  if (errorCode !== CAMPAIGN_CONCURRENCY_OVERCOMMIT_ERROR_CODE) {
    return null;
  }

  const activeCampaignRows = data?.activeCampaigns ?? data?.active_campaigns ?? [];
  return {
    errorCode,
    error: data?.error == null ? undefined : String(data.error),
    campaignType: (data?.campaignType ?? data?.campaign_type ?? null) as CampaignType | string | null,
    limit: toNumber(data?.limit),
    activeConcurrency: toNumber(data?.activeConcurrency ?? data?.active_concurrency),
    requestedConcurrency: toNumber(data?.requestedConcurrency ?? data?.requested_concurrency),
    projectedConcurrency: toNumber(data?.projectedConcurrency ?? data?.projected_concurrency),
    excessConcurrency: toNumber(data?.excessConcurrency ?? data?.excess_concurrency),
    campaign: normalizeCampaignRow(data?.campaign),
    activeCampaigns: activeCampaignRows
      .map((row) => normalizeCampaignRow(row))
      .filter((row): row is CampaignConcurrencyConflictRow => Boolean(row)),
  };
}
